import { useCallback } from "react";
import { queries } from "../../shared/queries";
import type { DisconnectImpact } from "../components/DisconnectWarning";
import { groupRemovalDisconnects } from "../lib/groupRemovalDisconnects";
import { useGroupMemberships } from "./useGroupMemberships";
import { useQuery } from "@rocicorp/zero/react";

// Client-side preview of what removing members from a group will disconnect.
// Best-effort: RBAC-filtered queries can under-list, so the server drop stays
// authoritative.
export function useGroupRemovalDisconnectImpact(
  userGroupIds: readonly string[]
) {
  const enabled = userGroupIds.length > 0;
  const [connectorRows, connectorsResult] = useQuery(
    enabled
      ? queries.groupConnectors({ userGroupIds: [...userGroupIds] })
      : undefined
  );
  const [usageSpaces, usageResult] = useQuery(
    enabled ? queries.sharedSpaceConnectorUsages() : undefined
  );
  const [gitRows, gitResult] = useQuery(
    enabled
      ? queries.groupConnectorGitRemoteUsage({
          userGroupIds: [...userGroupIds],
        })
      : undefined
  );
  const [spaces, spacesResult] = useQuery(
    queries.spaces({ settingsView: true })
  );
  const { userGroupIds: userGroupMemberships, loaded: membershipsLoaded } =
    useGroupMemberships();

  const computeImpact = useCallback(
    (groupId: string, removedUserIds: readonly string[]): DisconnectImpact => {
      if (removedUserIds.length === 0)
        return { workflows: [], gitSpaceNames: [] };
      return groupRemovalDisconnects({
        groupId,
        removedUserIds,
        connectorRows: connectorRows ?? [],
        usageSpaces: usageSpaces ?? [],
        gitRows: gitRows ?? [],
        spaces: spaces ?? [],
        userGroupMemberships,
      });
    },
    [connectorRows, usageSpaces, gitRows, spaces, userGroupMemberships]
  );
  const loaded =
    !enabled ||
    (membershipsLoaded &&
      [connectorsResult, usageResult, gitResult, spacesResult].every(
        (result) => result.type === "complete"
      ));
  return [computeImpact, loaded] as const;
}
